import { Button, FileUpload, showSnackbar, Table } from "@cofano/cck";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { useState } from "react";
import "../../styling/style.scss";
import { Language } from "utils";
import { CustomDialog } from "components/dialogs/Dialogs";
import { intl } from "LocaleWrapper";
import { gql } from "__generated__/gql";
import { useMutation } from "@apollo/client";
import { CallType, CargoInput } from "__generated__/graphql";
import { saveAs } from "file-saver";
import { CargoValidate, CargoExcel } from "types/index";

const CargoImportDialog = ({ open, onClose }: { open: boolean, onClose: (val: boolean) => void }) => {
	const { instance } = useCoastguard();

	const [ validationList, setValidationList ] = useState<CargoValidate[]>([]);
	const [ importCargos, { loading } ] = useMutation(CARGO_IMPORT_MUTATION);

	const upload = (files: File[]) => {
		const formData = new FormData();
		formData.append("uploadedFile", files[0]);

		instance.fetch("/cargos/upload/validate", {
			method: "POST",
			body: formData,
			headers: {
				accept: "application/json",
			},
		}).then(response => response.json().then((cargos: CargoValidate[]) => {
			setValidationList(cargos);
		}));
	};

	const createCargos = () => {
		const cargos = validationList.map(({ cargo }) => ({
			id: 0,
			cargoNumber: cargo.cargoNumber,
			nominationNumber: cargo.nominationNumber,
			product: cargo.product && {
				id: cargo.product,
			},
			productDetails: cargo.productDetails,
			loadingReference: cargo.loadingReference,
			dischargingReference: cargo.dischargingReference,
			charterCreditor: cargo.charterCreditor,
			charterDebtor: cargo.charterDebtor,
			charterDebtorType: cargo.charterDebtorType,
			charterIdentifier: cargo.charterIdentifier,
			consignee: cargo.consignee && {
				id: cargo.consignee,
			},
			consignor: cargo.consignor && {
				id: cargo.consignor,
			},
			supplier: cargo.supplier && {
				id: cargo.supplier,
			},
			containerOperator: cargo.containerOperator && {
				id: cargo.containerOperator,
			},
			deliveryCondition: cargo.deliveryCondition && {
				id: cargo.deliveryCondition,
			},
			actions: processActions(cargo),
		}));
		importCargos({ variables: { input: cargos as CargoInput } }).then(response => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFULLY_CREATED_X_CARGOS" }).replace("_X_", response.data!.importCargos.length.toString()), { variant: "success" });
			onClose(true);
		});
	};

	const processActions = (cargo: CargoExcel) => {
		const actions = [];
		actions.push(processAction(cargo.actionOneType, cargo.actionOneLocation, cargo.actionOneDate, cargo.actionOneWindowStart, cargo.actionOneWindowEnd, cargo.actionOneWeight, cargo.actionOneVolume));
		actions.push(processAction(cargo.actionTwoType, cargo.actionTwoLocation, cargo.actionTwoDate, cargo.actionTwoWindowStart, cargo.actionTwoWindowEnd, cargo.actionTwoWeight, cargo.actionTwoVolume));
		if (cargo.actionThreeType) {
			actions.push(processAction(cargo.actionThreeType, cargo.actionThreeLocation, cargo.actionThreeDate, cargo.actionThreeWindowStart, cargo.actionThreeWindowEnd, cargo.actionThreeWeight, cargo.actionThreeVolume));
		}
		if (cargo.actionFourType) {
			actions.push(processAction(cargo.actionFourType, cargo.actionFourLocation, cargo.actionFourDate, cargo.actionFourWindowStart, cargo.actionFourWindowEnd, cargo.actionFourWeight, cargo.actionFourVolume));
		}
		if (cargo.actionFiveType) {
			actions.push(processAction(cargo.actionFiveType, cargo.actionFiveLocation, cargo.actionFiveDate, cargo.actionFiveWindowStart, cargo.actionFiveWindowEnd, cargo.actionFiveWeight, cargo.actionFiveVolume));
		}
		return actions;
	};

	const processAction = (type: CallType, locationId: number, date: string, windowStart: string, windowEnd: string, weight: number, volume: number) => {
		return {
			type,
			location: {
				id: locationId,
			},
			date,
			windowStart,
			windowEnd,
			weight,
			volume,
		};
	};

	const downloadTemplate = () => {
		instance.fetchBlob("/cargoimporttemplate", {
			method: "GET",
			headers: {
				Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			},
		}).then(async (blob) => {
			saveAs(blob, "cargotemplate.xlsx");
		});
	};

	return <CustomDialog
		maxWidth="xl"
		open={open}
		title={intl.formatMessage({ id: "UPLOAD_USING_EXCEL" })}
		actions={<>
			<Button onClick={() => createCargos()} disabled={validationList.length === 0 || validationList.some(v => !v.valid)}>
				{intl.formatMessage({ id: "UPLOAD" })}
			</Button>
		</>}
		onClose={() => onClose(false)}>
		<Button variant="text" onClick={downloadTemplate}>
			{intl.formatMessage({ id: "DOWNLOAD_TEMPLATE" })}
		</Button>
		<div className="cargo-import-upload">
			<FileUpload key={JSON.stringify(validationList)}
				maxNumberOfFiles={1}
				multiple={false}
				onChange={upload}
				accept={{
					"application/vnd.ms-excel": [ ".xlsx" ],
				}} />
		</div>
		{validationList.length > 0 && !loading && <div className="validation-list">
			<Table
				id={"cargo-validation"}
				highlightRow={(row: CargoValidate) => row.valid ? "SUCCESS" : "ERROR"}
				onGetRowId={row => row.cargo.cargoNumber}
				columns={[
					{
						id: "cargo.cargoNumber",
						title: Language("CARGO_NUMBER"),
					},
					{
						id: "cargo.productName",
						title: Language("PRODUCT"),
					},
					{
						id: "bosExceptions",
						title: "Validation errors",
						width: 600,
						render: (row: CargoValidate) => {
							if (row.bosExceptions.length > 0) {
								return <ul style={{ whiteSpace: "normal" }}>
									{row.bosExceptions.map(exception => <li>{exception.error.code} - {exception.error.message}{exception.parameters?.[0] && `- ${exception.parameters[0].message}`}</li>)}
								</ul>;
							} else if (row.otherExceptions.length > 0) {
								return Language("UNKNOWN_ERROR_OCCURRED");
							}
						},
					},
					{
						id: "cargo.nominationNumber",
						title: Language("NOMINATION_NUMBER"),
					},
					{
						id: "cargo.consignorName",
						title: Language("CARGO_CONSIGNOR"),
					},
					{
						id: "cargo.consigneeName",
						title: Language("ORDER_CONSIGNEE"),
					},
					{
						id: "cargo.supplierName",
						title: Language("SUPPLIER"),
					},
					{
						id: "cargo.containerOperatorName",
						title: Language("CONTAINER_OPERATOR"),
					},
				]}
				//@ts-ignore
				content={validationList} />
		</div>}
	</CustomDialog>;
};

const CARGO_IMPORT_MUTATION = gql(`#graphql
	mutation importCargos($input: [CargoInput!]!) {
		importCargos(input: $input) {
			id
		}
	}
`);

export default CargoImportDialog;
